import { Datum } from "@nivo/line";
import { IInternalUser } from "../atoms/UserAtom";
import Configs from "../config";
import { IUserEmailRestrictions, IUserIRoute } from "../generated/Api";
import { ICustomCopyTemplates, User } from "../types/UserTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";

export interface CreateUserResponse {
  userUid: string;
  phone: string;
  email: string;
  lastName: string;
  firstName: string;
  avatar: null;
  options: {
    twoStepVerifiation: boolean;
    desktopNotifications: boolean;
    receiveNewFeatures: boolean;
    firstLineAsSubject: boolean;
    extraCarriage: boolean;
  };
  _id: string;
}
export interface GetUserResponse {
  status: string;
  data: IUserIRoute & {
    billing: {
      admins?: number;
      externalUsers?: number;
      active?: boolean;
      testDriveExpiration?: number;
      testDriveActivated?: boolean;
    };
  };
}

export async function createUser(user: Partial<User>): Promise<User | null> {
  try {
    const response: Response = await fetch(`${Configs.SERVER_URL}/user`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(user),
    });

    if (response.status === 201) {
      const responseObject = await response.json();

      return responseObject.data;
    }

    return null;
  } catch (err) {
    return null;
  }
}

async function getInternalUserManagedUsers(internalUserUid: string) {
  try {
    const token = localStorageGetItem("INTERNAL_TOKEN");
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/internalUser/managed/${internalUserUid}`,
      {
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === 200) {
      const responseObject: { data: { managedUsers: IUserIRoute[] } } =
        await response.json();
      return responseObject.data;
    }
    return null;
  } catch (err) {
    return null;
  }
}

async function getInternalUser(userUid: string): Promise<IInternalUser | null> {
  try {
    const token = localStorageGetItem("INTERNAL_TOKEN");
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/internalUserUid/${userUid}`,
      {
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === 200) {
      const responseObject = await response.json();
      return responseObject.data.internalUser;
    }
    return null;
  } catch (err) {
    return null;
  }
}

async function getInternalUserToken(internalUserUid: string, userUid: string) {
  try {
    const token = localStorageGetItem("INTERNAL_TOKEN");
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/internalUser/token`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          internalUserUid,
          userUid,
        }),
      }
    );
    if (response.status === 200) {
      const responseObject: { data: { token: string | null } } =
        await response.json();
      return responseObject.data;
    }
    return null;
  } catch (err) {
    return null;
  }
}

export async function getUser(uid?: string): Promise<
  | (IUserIRoute & {
      billing: {
        admins?: number;
        externalUsers?: number;
        active?: boolean;
        testDriveExpiration?: number;
        testDriveActivated?: boolean;
      };
    })
  | null
> {
  try {
    const userUid = uid || localStorageGetItem("USER_UID");

    const token = localStorageGetItem("USER_TOKEN");
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/userUid/${userUid}`,
      {
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === 200) {
      const responseObject: GetUserResponse = await response.json();
      return responseObject.data;
    }
    return null;
  } catch (err) {
    return null;
  }
}

export async function updateUser(
  user: Partial<IUserIRoute>,
  params?: {
    //IUserIRoute,
    internalUserUid?: string;
  }
): Promise<IUserIRoute | null> {
  try {
    const token = localStorageGetItem("USER_TOKEN");

    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user${
        params?.internalUserUid
          ? `?internalUserUid=${params?.internalUserUid}`
          : ""
      }`,
      {
        method: "PUT",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      }
    );

    if (response.status === 200) {
      const responseObject: IUserIRoute = await response.json();
      return responseObject;
    }

    return null;
  } catch (err) {
    return null;
  }
}

export const setExtraCarriage = async (value: boolean): Promise<Response> => {
  const userUid = localStorageGetItem("USER_UID");
  const user = await getUser(userUid as string);

  if (user) {
    const allTags = await fetch(`${Configs.SERVER_URL}/user/${user._id}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...user,
        options: {
          ...user.options,
          extraCarriage: value,
        },
      }),
    });

    return allTags;
  }
  throw new Error();
};

export const setFirstLineAsSubject = async (
  value: boolean
): Promise<Response> => {
  const userUid = localStorageGetItem("USER_UID");
  const user = await getUser(userUid as string);
  if (user) {
    const allTags = await fetch(`${Configs.SERVER_URL}/user/${user._id}`, {
      method: "PUT",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...user,
        options: {
          ...user.options,
          firstLineAsSubject: value,
        },
      }),
    });
    return allTags;
  }
  throw new Error();
};

export interface EmailOverviewResponse {
  status: string;
  data: EmailOverviewResponseData;
}

export interface EmailOverviewResponseData {
  pending: WebhookOverviewInfo[];
  delivered: WebhookOverviewInfo[];
  opened: WebhookOverviewInfo[];
  failed: WebhookOverviewInfo[];
  unsubbed: WebhookOverviewInfo[];
  clicks: WebhookOverviewInfo[];
  spam: WebhookOverviewInfo[];
  totalSent: number;
  uniqueClicks: number;
  uniqueOpens: number;
  uniqueSpams?: number;

  // @nivo data generated on the backend
  clickData: Datum[];
  openData: Datum[];
}

// export interface WebhookOverviewInfo {
//   contactId: string;
//   eventDate: string;
//   emailHash: string;
// }

export interface WebhookOverviewInfo {
  length: number;
}

async function getEmailOverview(userUid: string) {
  try {
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/overview/${userUid}`,
      {}
    );

    if (response.status === 200) {
      const responseObject: EmailOverviewResponse = await response.json();

      return responseObject.data;
    }

    return null;
  } catch (err) {
    return null;
  }
}

async function getAdminAllUsers(userUid: string) {
  const response: Response = await fetch(`${Configs.SERVER_URL}/user/admin`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      userUid,
    }),
  });

  if (response.status === 200) {
    const responseObject: { data: IUserIRoute[] } = await response.json();

    return responseObject.data;
  }

  return null;
}

export async function getEmailRestrictions() {
  const userUid = localStorageGetItem("USER_UID");

  try {
    const response: Response = await fetch(
      `${Configs.SERVER_URL}/user/${userUid}/restrictions`
    );
    if (response.status === 200) {
      const responseObject: { data: IUserEmailRestrictions } =
        await response.json();

      return responseObject.data;
    }

    return null;
  } catch (err) {
    return null;
  }
}

export async function addEmailRestrictions(
  data: Partial<IUserEmailRestrictions>
) {
  const userUid = localStorageGetItem("USER_UID");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/user/${userUid}/restrictions/add`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
  if (response.status === 200) {
    const responseObject: DefaultResponse<{ count: number }> =
      await response.json();

    return responseObject.status === "ok";
  }

  return false;
}

export async function removeEmailRestrictions(
  data: Partial<IUserEmailRestrictions>
) {
  const userUid = localStorageGetItem("USER_UID");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/user/${userUid}/restrictions/remove`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
  if (response.status === 200) {
    const responseObject: DefaultResponse<{ count: number }> =
      await response.json();

    return responseObject.status === "ok";
  }

  return false;
}

export async function getFreeFavoriteTime(previousFavoriteDate?: string) {
  const token = localStorageGetItem("USER_TOKEN");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/user/favoriteScheduleTimes?previousFavoriteDate=${
      previousFavoriteDate || ""
    }`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 200) {
    const responseObject: { data: { favoriteTime: string } } =
      await response.json();
    console.log(responseObject);
    return responseObject.data;
  }
}

async function getCustomCopyTemplates() {
  const token = localStorageGetItem("USER_TOKEN");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/customCopyTemplates`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 200) {
    const responseObject: { data: ICustomCopyTemplates[] } =
      await response.json();
    console.log(responseObject);
    return responseObject.data;
  }
}

async function createCustomCopyTemplate(params: {
  title: string;
  template: string;
}) {
  const { title, template } = params;

  const token = localStorageGetItem("USER_TOKEN");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/customCopyTemplates`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        template,
      }),
    }
  );

  if (response.status === 200) {
    const responseObject: { data: ICustomCopyTemplates } =
      await response.json();
    console.log(responseObject);
    return responseObject.data;
  }
}

async function deleteCustomCopyTemplate(params: { _id: string }) {
  const { _id } = params;

  const token = localStorageGetItem("USER_TOKEN");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/customCopyTemplates/${_id}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.status === 200) {
    const responseObject: { data: ICustomCopyTemplates } =
      await response.json();
    console.log(responseObject);
    return responseObject.data;
  }
}

async function updateCustomCopyTemplate(params: {
  _id: string;
  title: string;
  template: string;
  isDefault?: boolean;
}) {
  const { title, template, isDefault, _id } = params;

  const token = localStorageGetItem("USER_TOKEN");

  const response: Response = await fetch(
    `${Configs.SERVER_URL}/customCopyTemplates/${_id}`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        template,
        isDefault,
      }),
    }
  );

  if (response.status === 200) {
    const responseObject: { data: ICustomCopyTemplates } =
      await response.json();
    console.log(responseObject);
    return responseObject.data;
  }
}

export const UserAPI = {
  getUser,
  getCustomCopyTemplates,
  createCustomCopyTemplate,
  deleteCustomCopyTemplate,
  updateCustomCopyTemplate,
  getEmailOverview,
  getAdminAllUsers,
  getInternalUser,
  getFreeFavoriteTime,
  getInternalUserManagedUsers,
  getInternalUserToken,
  removeEmailRestrictions,
};
